import React from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';

import type {
  ComponentRenderProps,
  TextProps,
} from '../../../types/dynamic_views';

// Build CSS classes
const variantClasses = {
  body: 'text-base leading-relaxed',
  caption: 'text-sm leading-normal',
  subtitle: 'text-lg leading-relaxed',
};

const sizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
};

// Using design system tokens for dark mode support
const colorClasses = {
  primary: 'text-fg-primary',
  secondary: 'text-fg-tertiary',
  neutral: 'text-fg-primary',
  muted: 'text-fg-quaternary',
};

const weightClasses = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export const TextComponent: React.FC<ComponentRenderProps> = ({
  component,
  className = '',
}) => {
  const { currentOrg } = useOrg();
  const props = component.props as TextProps;
  const {
    content,
    variant = 'body',
    size = 'md',
    color = 'neutral',
    weight = 'normal',
    align = 'left',
  } = props;

  const allClasses = [
    variantClasses[variant] || variantClasses.body,
    sizeClasses[size] || sizeClasses.md,
    colorClasses[color] || colorClasses.neutral,
    weightClasses[weight] || weightClasses.normal,
    alignClasses[align] || alignClasses.left,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={allClasses}
      data-component-id={component.id}
      data-variant={variant}
    >
      <MarkdownContent
        content={content}
        className='[&_p]:mb-2 [&_p:last-child]:mb-0'
        orgId={currentOrg?.id}
      />
    </div>
  );
};

export default TextComponent;
